import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, BookOpen, Flag, Target, MessageCircle, Trophy } from 'lucide-react';
import { Section } from './Section';
import { Activity } from './Activity';

const CATEGORY_LABEL = {
  mental_health: 'Mental Health',
  financial: 'Financial Wellness',
  relationships: 'Relationships',
  fatherhood: 'Fatherhood',
  career: 'Career',
  physical: 'Physical Wellness',
  faith: 'Faith & Purpose',
};

export const StepWelcome = ({ lesson }) => (
  <div className="text-center pt-6">
    <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-3">
      {CATEGORY_LABEL[lesson.category] || 'Course'} · {lesson.duration}
    </p>
    <h1 className="font-heading text-4xl sm:text-5xl leading-tight text-navy">{lesson.title}</h1>
    <p className="text-navy/70 mt-4 text-lg leading-relaxed max-w-md mx-auto">{lesson.welcome?.intro || lesson.summary}</p>
    {lesson.welcome?.what_youll_gain?.length > 0 && (
      <div className="mt-10 bg-white rounded-3xl p-7 card-soft text-left max-w-md mx-auto">
        <p className="text-[10px] uppercase tracking-widest text-navy/60 font-semibold mb-4">What you&apos;ll gain</p>
        <ul className="space-y-3">
          {lesson.welcome.what_youll_gain.map((g, i) => (
            <li key={i} className="flex gap-3 text-navy/85 leading-relaxed"><Check size={18} className="text-gold mt-1 flex-shrink-0"/>{g}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export const StepWhy = ({ lesson }) => (
  <div className="pt-4">
    <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mb-3 flex items-center gap-2"><BookOpen size={12}/>Why this matters</p>
    <h2 className="font-heading text-3xl sm:text-4xl text-navy mb-6 leading-tight">Why this matters right now.</h2>
    <p className="text-navy/85 leading-[1.8] text-[17px]">{lesson.welcome?.why_it_matters}</p>
  </div>
);

export const StepSection = ({ section }) => (
  <div className="pt-4">
    <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold mb-3 flex items-center gap-2"><Sparkles size={12}/>Core Learning</p>
    <Section s={section}/>
  </div>
);

export const StepTherapist = ({ lesson }) => (
  <div className="pt-4">
    <div className="bg-navy text-white rounded-3xl p-8 card-soft">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen size={16} className="text-gold"/>
        <p className="text-[10px] uppercase tracking-widest text-gold font-semibold">A deeper perspective</p>
      </div>
      <p className="text-white/90 leading-[1.85] text-[17px]">{lesson.therapist_perspective}</p>
    </div>
  </div>
);

export const StepEvidence = ({ ev }) => (
  <div className="pt-4">
    <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mb-3">Evidence Spotlight</p>
    <h2 className="font-heading text-3xl sm:text-4xl text-navy mb-6 leading-tight">One research finding.</h2>
    <div className="bg-beige rounded-3xl p-8">
      <p className="text-navy leading-[1.75] text-[17px]">{ev.finding}</p>
      {ev.source && (
        ev.url ? (
          <a href={ev.url} target="_blank" rel="noopener noreferrer" className="text-xs text-navy/60 mt-6 inline-block uppercase tracking-widest hover:text-navy">
            Source: {ev.source} ↗
          </a>
        ) : (
          <p className="text-xs text-navy/60 mt-6 uppercase tracking-widest">Source: {ev.source}</p>
        )
      )}
    </div>
  </div>
);

export const StepScenario = ({ sc }) => {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="pt-4">
      <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mb-3">Real Life</p>
      <h2 className="font-heading text-3xl sm:text-4xl text-navy mb-6 leading-tight">A scenario to sit with.</h2>
      <div className="bg-white border border-navy/10 rounded-3xl p-7">
        <p className="text-navy/85 leading-[1.75] italic text-[17px]">&ldquo;{sc.setup}&rdquo;</p>
        {sc.question && <p className="font-heading text-xl text-navy mt-5">{sc.question}</p>}
      </div>
      {sc.guidance && (
        !revealed ? (
          <button
            onClick={() => setRevealed(true)}
            data-testid="scenario-reveal"
            className="mt-4 w-full bg-navy/5 hover:bg-navy/10 rounded-2xl p-4 text-sm text-navy/70 transition-smooth">
            Show a way through →
          </button>
        ) : (
          <div className="mt-4 bg-gold/10 border-l-4 border-gold rounded-r-3xl p-6" data-testid="scenario-guidance">
            <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mb-2">Something to sit with</p>
            <p className="text-navy/85 leading-[1.75] text-[16px]">{sc.guidance}</p>
          </div>
        )
      )}
    </div>
  );
};

export const StepActivity = ({ activity, reflection, setReflection }) => (
  <div className="pt-4">
    <Activity activity={activity} reflection={reflection} setReflection={setReflection}/>
  </div>
);

export const StepChallenge = ({ ap }) => (
  <div className="pt-4">
    <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mb-3 flex items-center gap-2"><Flag size={12}/>Your Challenge</p>
    <h2 className="font-heading text-3xl sm:text-4xl text-navy mb-6 leading-tight">One real thing to try.</h2>
    <div className="flex items-start gap-4 bg-gold/10 border border-gold/30 rounded-3xl p-6">
      <div className="w-10 h-10 rounded-2xl bg-gold/20 flex items-center justify-center shrink-0">
        <Flag size={18} className="text-gold"/>
      </div>
      <div>
        <p className="font-heading text-xl text-navy leading-snug">{ap.today}</p>
        {ap.why && <p className="text-navy/70 mt-3 text-[15px] leading-relaxed">{ap.why}</p>}
      </div>
    </div>
  </div>
);

export const StepComplete = ({ lesson, onCoach, onFinish }) => (
  <div className="pt-6 text-center">
    <div className="w-20 h-20 mx-auto rounded-full bg-gold/15 flex items-center justify-center mb-6">
      <Trophy size={36} className="text-gold" strokeWidth={1.5}/>
    </div>
    <h2 className="font-heading text-3xl sm:text-4xl text-navy mb-3">Lesson Complete</h2>
    <p className="text-navy/70 leading-relaxed max-w-md mx-auto">Well done. That&apos;s another rep for the version of you you&apos;re becoming.</p>

    {lesson.ai_coach_prompts?.length > 0 && (
      <div className="mt-10 text-left max-w-md mx-auto">
        <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold mb-3 flex items-center gap-2"><Target size={12}/>Continue with your AI Coach</p>
        <div className="space-y-2">
          {lesson.ai_coach_prompts.map((p, i) => (
            <Link
              key={i}
              to={`/app/coach?prompt=${encodeURIComponent(p)}`}
              data-testid={`coach-prompt-${i}`}
              className="block bg-white border border-navy/10 rounded-2xl p-4 hover:bg-beige/50 transition-smooth">
              <span className="text-navy leading-relaxed">&ldquo;{p}&rdquo;</span>
              <span className="block text-xs text-gold mt-1">Ask the Coach →</span>
            </Link>
          ))}
        </div>
      </div>
    )}

    <div className="mt-10 max-w-md mx-auto space-y-3">
      <button
        onClick={onCoach}
        data-testid="lesson-continue-coach"
        className="w-full bg-navy text-white rounded-full py-4 font-medium hover:bg-navy-soft transition-smooth flex items-center justify-center gap-2">
        <MessageCircle size={18}/> Continue with AI Coach
      </button>
      <button
        onClick={onFinish}
        data-testid="lesson-return-course"
        className="w-full border border-navy/15 text-navy rounded-full py-4 font-medium hover:bg-beige transition-smooth">
        Return to course
      </button>
    </div>
  </div>
);
